import {
  ICreateHostProfileRequest,
  IUpdateHostProfileRequest,
  IHostProfile,
  IStripeOnboardingResponse,
} from '../interfaces/heavyDutyMachinery/IHostProfile';
import { apiService } from './ApiService';
import { API_ENDPOINTS } from '../constants/apiEndpoints';
import { HTTP_METHODS } from '../constants/httpMethods';

const heavyDutyMachineryBaseUrl = process.env.REACT_APP_HEAVY_DUTY_MACHINERY_API_URL || '';

const isNotFound = (error?: string): boolean => !!error && error.startsWith('HTTP Error: 404');

/** Returns undefined (not an error) when the caller has no HostProfile yet -- callers use this to decide whether to show the onboarding CTA/wizard. */
export const getMyHostProfile = async (): Promise<IHostProfile | undefined> => {
  const response = await apiService<IHostProfile>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_HOST_PROFILE_ME,
    method: HTTP_METHODS.GET,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  if (isNotFound(response.error)) return undefined;
  throw new Error(response.error || 'Failed to load host profile');
};

export const createHostProfile = async (request: ICreateHostProfileRequest): Promise<IHostProfile> => {
  const response = await apiService<IHostProfile>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_HOST_PROFILE_CREATE,
    method: HTTP_METHODS.POST,
    body: { ...request },
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to create host profile');
};

export const updateMyHostProfile = async (request: IUpdateHostProfileRequest): Promise<IHostProfile> => {
  const response = await apiService<IHostProfile>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_HOST_PROFILE_ME,
    method: HTTP_METHODS.PATCH,
    body: { ...request },
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to update host profile');
};

/**
 * Creates the host's Stripe Connect Express account the first time it's
 * called, then always returns a fresh Account Session client secret -- feed
 * accountSessionClientSecret into Connect.js' fetchClientSecret. Also the
 * right call once the host is Verified: the same session covers the
 * embedded balances/payouts/payments components too, not just onboarding.
 */
export const startHostVerification = async (): Promise<IStripeOnboardingResponse> => {
  const response = await apiService<IStripeOnboardingResponse>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_HOST_PROFILE_VERIFICATION_START,
    method: HTTP_METHODS.POST,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to start verification');
};
